import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
// import * as htmlToImage from 'html-to-image';
// import jsPDF from 'jspdf';
import { PDFDownloadLink, usePDF } from '@react-pdf/renderer';
import { Document, Page, pdfjs } from 'react-pdf';
import InvoicePdf from './InvoicePdf';
import useInput from 'lib/hooks/useInput';
import { throttle } from 'lodash';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import MuiButton from 'components/common/button/MuiButton';
import DownloadIcon from 'components/base/icons/DownloadIcon';
import { useShallowAppSelector } from 'store/hooks';
import { useDidUpdateEffect } from 'lib/utils';
import T from 'components/common/text/T';
import { useTranslation } from 'react-i18next';
import { IinvoiceInformation, IindicationProjectUnit } from 'lib/types/invoiceTypes';

export interface InvoiceViewerProps {
  invoiceInformation: IinvoiceInformation;
  indicationProjectUnitList: IindicationProjectUnit[];
}

function InvoiceViewer({ invoiceInformation, indicationProjectUnitList }: InvoiceViewerProps) {
  const { language, invoiceDataPending } = useShallowAppSelector(state => ({
    language: state.base.language,
    invoiceDataPending: state.invoice.invoice.pending,
  }));
  const { t } = useTranslation();
  const invoiceScrollRef = useRef<HTMLDivElement | null>(null);
  const page = useInput(null);
  const totalPage = useInput(null);
  const isLoadedData = invoiceInformation && indicationProjectUnitList;
  const isPossibleDownload = isLoadedData.length > 0 && !invoiceDataPending;

  const [instance, updateInstance] = usePDF({
    document: (
      <InvoicePdf
        invoiceInformation={invoiceInformation}
        indicationProjectUnitList={indicationProjectUnitList}
        language={language}
      />
    ),
  });

  const [numPages, setNumPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);

  // SECTION: function
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const handlePrevPage = () => {
    if (invoiceScrollRef.current) {
      let targetPdfHeight =
        invoiceScrollRef.current?.querySelector('.react-pdf__Document')?.clientHeight || 0;
      targetPdfHeight = targetPdfHeight + 2;
      const pageScrollTop = targetPdfHeight * (pageNumber - 2);
      invoiceScrollRef.current.scrollTop = pageScrollTop;
    }

    setPageNumber(pageNumber - 1);
  };

  const handleNextPage = () => {
    if (invoiceScrollRef.current) {
      let targetPdfHeight =
        invoiceScrollRef.current?.querySelector('.react-pdf__Document')?.clientHeight || 0;
      targetPdfHeight = targetPdfHeight + 2;
      const pageScrollTop = targetPdfHeight * pageNumber;
      invoiceScrollRef.current.scrollTop = pageScrollTop;
    }

    setPageNumber(pageNumber + 1);
  };

  const handlePdfScroll = throttle((e: any) => {
    // console.log('e', e);
    const currentScrollTop = invoiceScrollRef.current?.scrollTop || 0;
    let targetPdfHeight =
      invoiceScrollRef.current?.querySelector('.react-pdf__Document')?.clientHeight || 0;
    targetPdfHeight = targetPdfHeight + 2;

    const currentPageNumber = Math.floor(currentScrollTop / targetPdfHeight) + 1;
    setPageNumber(currentPageNumber);
  }, 300);

  // SECTION: DidMount
  useEffect(() => {
    pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
  }, []);

  // SECTION: DidUpdate
  useDidUpdateEffect(() => {
    if (isLoadedData) updateInstance();
  }, [isLoadedData]);

  const list = ['&amp;'];

  return (
    <StyledInvoiceViewer data-component-name="InvoiceViewer">
      <div className="invoiceViewer__wrapper">
        <div className="invoiceViewer__container" ref={invoiceScrollRef} onScroll={handlePdfScroll}>
          <div className="invoiceViewer__box">
            {/* <PDFViewer width={600} height={850} style={{ flex: 1 }} showToolbar={false}>
            <InvoicePdf />
          </PDFViewer> */}

            {/* invoiceInformation &&
              indicationProjectUnitList && */}
            {instance.url &&
              Array.from({ length: numPages }).map((item, idx) => (
                <Document
                  key={idx}
                  // file={require('./sample.pdf')}
                  file={instance.url}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={error => console.log(error.message)}
                >
                  <Page pageNumber={idx + 1} />
                </Document>
              ))}
          </div>
        </div>

        <div className="invoiceViewer__page_container">
          <div className="invoiceViewer__page_box">
            <button onClick={handlePrevPage} disabled={pageNumber <= 1} className="btn-reset">
              <ArrowBackIosIcon fontSize="inherit" />
            </button>
            <p>
              {pageNumber} / {numPages}
            </p>
            <button
              onClick={handleNextPage}
              disabled={pageNumber >= numPages}
              className="btn-reset"
            >
              <ArrowForwardIosIcon fontSize="inherit" />
            </button>
          </div>
        </div>

        <div className="invoiceViewer__btn_box">
          <MuiButton
            config={{ width: 280 }}
            disabled={!isPossibleDownload}
            variant="contained"
            color="secondary"
            className="md border-radius-round inset-shadow-default"
          >
            {isPossibleDownload ? (
              <PDFDownloadLink
                fileName="pdf_name"
                document={
                  <InvoicePdf
                    language={language}
                    invoiceInformation={invoiceInformation}
                    indicationProjectUnitList={indicationProjectUnitList}
                  />
                }
                className="invoiceViewer__download_link"
              >
                <DownloadIcon color="white" width={18} /> <T>GLOBAL_DOWNLOAD</T>
              </PDFDownloadLink>
            ) : (
              <>
                <DownloadIcon color="white" width={18} /> <T>GLOBAL_DOWNLOAD</T>
              </>
            )}
          </MuiButton>
        </div>
      </div>

      {/* <h1>Invoice</h1>

      <div className="invoiceViewer__container" ref={invoicePdfRef}>
        <p>
          Lorem ipsum dolor sit amet consectetur adipisicing elit. Debitis corporis aspernatur
          quisquam cupiditate illo sequi, nemo similique quas iusto magni eaque dignissimos odio
          suscipit reiciendis aliquam nisi amet! Nostrum, velit.
        </p>
      </div>

      <div className="invoiceViewer__btn_box">
        <button onClick={handleDownloadPdf}>Download to Pdf</button>
      </div> */}
    </StyledInvoiceViewer>
  );
}

const StyledInvoiceViewer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  /* height: 100%; */
  height: 1000px;
  padding: 40px 0;

  .invoiceViewer__container {
    height: 841px;
    background-color: white;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.16);
    overflow-y: auto;

    .invoiceViewer__box {
      width: 595px;
    }
    .react-pdf__Document {
      &:not(:last-child) {
        border-bottom: 2px solid lightgray;
      }
    }
  }

  .invoiceViewer__page_container {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 10px 0;
    font-size: 12px;
    /* border: 1px solid #ddd; */
    .invoiceViewer__page_box {
      display: flex;
      align-items: center;
      padding: 2px 5px;
      background-color: white;
      border-radius: 5px;
      line-height: 1;
      p {
        margin: 0 5px;
      }
      svg {
        font-size: 12px;
      }
      button {
        padding: 5px 3px;
      }
    }
  }

  .invoiceViewer__btn_box {
    display: flex;
    justify-content: center;
    .invoiceViewer__download_link {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 40px;
    }
    svg {
      margin-right: 10px;
    }
  }
`;

export default React.memo(InvoiceViewer);

/* margin: 3px 4px 13px; */
/* margin: -3px -4px -13px; */
